import { useState } from "react";

// Número de WhatsApp no formato internacional (sem + ou espaços)
const WHATSAPP_NUMBER = "5547992561262";

// Link direto para o WhatsApp (usado nos cards de contato)
const WHATSAPP_DIRECT = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Olá! Gostaria de solicitar um orçamento.")}`;

interface FormState {
  nome: string;
  email: string;
  mensagem: string;
}

export default function ContactSection() {
  const [form, setForm] = useState<FormState>({ nome: "", email: "", mensagem: "" });

  // Atualiza o estado conforme o usuário digita
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Ao enviar: monta a mensagem e abre o WhatsApp em nova aba
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Impede o comportamento padrão do formulário (recarregamento de página)
    e.preventDefault();

    // Monta a mensagem no formato exato solicitado
    const mensagem = `Nome Completo: ${form.nome} | E-mail: ${form.email} | Motivo do Contato: ${form.mensagem}`;

    // Codifica a mensagem para uso seguro na URL
    const mensagemCodificada = encodeURIComponent(mensagem);

    // Monta o link do WhatsApp e abre em nova aba
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${mensagemCodificada}`;
    window.open(whatsappUrl, "_blank");

    // Limpa o formulário após o envio
    setForm({ nome: "", email: "", mensagem: "" });
  };

  return (
    <section id="contato" className="py-24" style={{ backgroundColor: "#E5E7EB" }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-[#C9A24A] text-xs font-semibold tracking-widest uppercase mb-3 block">
            Fale conosco
          </span>
          <h2 className="text-[#1F1F1F] font-bold text-4xl md:text-5xl mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Entre em Contato
          </h2>
          <div className="w-16 h-1 mx-auto bg-[#C9A24A]" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Formulário */}
          <div className="bg-white rounded-2xl p-8">
            <h3 className="text-[#1F1F1F] font-bold text-2xl mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Solicite seu Orçamento
            </h3>

            {/* Formulário com apenas 3 campos obrigatórios */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">

              {/* Campo: Nome Completo */}
              <div>
                <label className="text-[#1F1F1F] text-sm font-medium mb-1 block">
                  Nome Completo <span className="text-[#C9A24A]">*</span>
                </label>
                <input
                  type="text"
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  required
                  placeholder="Seu nome completo"
                  className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm text-[#1F1F1F] placeholder-[#7A7A7A] focus:outline-none focus:border-[#C9A24A] bg-white"
                />
              </div>

              {/* Campo: E-mail */}
              <div>
                <label className="text-[#1F1F1F] text-sm font-medium mb-1 block">
                  E-mail <span className="text-[#C9A24A]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="seu@email.com"
                  className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm text-[#1F1F1F] placeholder-[#7A7A7A] focus:outline-none focus:border-[#C9A24A] bg-white"
                />
              </div>

              {/* Campo: Mensagem */}
              <div>
                <label className="text-[#1F1F1F] text-sm font-medium mb-1 block">
                  Mensagem <span className="text-[#C9A24A]">*</span>{" "}
                  <span className="text-[#7A7A7A] font-normal">({form.mensagem.length}/500)</span>
                </label>
                <textarea
                  name="mensagem"
                  value={form.mensagem}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="Descreva seu projeto ou dúvida..."
                  className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm text-[#1F1F1F] placeholder-[#7A7A7A] focus:outline-none focus:border-[#C9A24A] bg-white resize-none"
                />
                {form.mensagem.length >= 500 && (
                  <p className="text-red-500 text-xs mt-1">Limite de 500 caracteres atingido.</p>
                )}
              </div>

              {/* Botão de envio — abre WhatsApp com mensagem pronta */}
              <button
                type="submit"
                disabled={form.mensagem.length > 500}
                className="w-full bg-[#25D366] hover:bg-[#20b958] disabled:bg-[#7A7A7A] text-white font-semibold py-4 rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-sm"
              >
                <i className="ri-whatsapp-line text-lg" />
                Enviar via WhatsApp
              </button>
            </form>
          </div>

          {/* Informações de Contato */}
          <div className="flex flex-col gap-6">
            <div>
              <h3 className="text-[#1F1F1F] font-bold text-2xl mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Informações de Contato
              </h3>
            </div>

            {[
              {
                icon: "ri-whatsapp-line",
                title: "WhatsApp",
                value: "(47) 99256-1262",
                href: WHATSAPP_DIRECT,
                color: "#25D366",
              },
              {
                icon: "ri-mail-line",
                title: "E-mail",
                value: "construlifeprojetos@gmail.com",
                href: "mailto:construlifeprojetos@gmail.com",
                color: "#C9A24A",
              },
              {
                icon: "ri-instagram-line",
                title: "Instagram",
                value: "@construlifeprojetos",
                href: "https://instagram.com/construlifeprojetos",
                color: "#C9A24A",
              },
              {
                icon: "ri-map-pin-line",
                title: "Endereço",
                value: "Rua Soldado Moacir Pinheiro nº 279 Apto 301",
                href: null,
                color: "#C9A24A",
              },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-xl p-5 flex items-start gap-4">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{ backgroundColor: `${item.color}20` }}
                >
                  <i className={`${item.icon} text-xl`} style={{ color: item.color }} />
                </div>
                <div>
                  <p className="text-[#7A7A7A] text-xs mb-1">{item.title}</p>
                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-[#1F1F1F] font-semibold text-sm hover:text-[#C9A24A] cursor-pointer transition-colors"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-[#1F1F1F] font-semibold text-sm">{item.value}</p>
                  )}
                </div>
              </div>
            ))}

            <a
              href={WHATSAPP_DIRECT}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20b958] text-white font-bold py-4 rounded-xl transition-colors duration-200 cursor-pointer whitespace-nowrap text-[15px]"
            >
              <i className="ri-whatsapp-line text-xl" />
              Chamar no WhatsApp Agora
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
